import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { AppValidators } from 'src/app/common/appValidators';
import { InventoryCustomViewKey } from 'src/app/models/constants/inventoryCustomView.constants';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { InventoryFiltersModel } from 'src/app/models/filters/implementations/pageFilters/inventoryFilters.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { InventoryCustomViewOptions } from 'src/app/models/selectOptions/inventoryCustomViewOptions.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';

@Component({
  selector: 'app-inventory-filters',
  templateUrl: './inventory-filters.component.html',
  styleUrls: ['./inventory-filters.component.scss'],
})
export class InventoryFiltersComponent implements OnInit {
  @Input() public pageFilters: InventoryFiltersModel;

  public clearCustomViewSelection$ = new BehaviorSubject(undefined);

  public selectedOptions: ISelectOption[] = [];

  public searchBoxNewValue = new ReplaySubject<string | undefined>(1);
  public fromDateNewValue = new ReplaySubject<Date | undefined>(1);
  public toDateNewValue = new ReplaySubject<Date | undefined>(1);
  public customViewOptions: ISelectOption[] = InventoryCustomViewOptions;
  public selectedCustomOption?: ISelectOption;
  public searchValidators = [AppValidators.evenNumberOfQuoteMarks];

  private _customViewTriggered = false;

  constructor(
    private readonly _router: Router,
    public featureFlagService: FeatureFlagsService
  ) {}

  ngOnInit(): void {
    this.pageFilters.onUpdate$.subscribe((data) => {
      this.selectedOptions = this.pageFilters.getAllSelectedOptions();

      this.searchBoxNewValue.next(data.search);
      this.fromDateNewValue.next(data.fromDate);
      this.toDateNewValue.next(data.toDate);

      if (this._customViewTriggered) {
        this._customViewTriggered = false;
      } else {
        this.clearCustomViewSelection$.next(undefined);
        this.selectedCustomOption = undefined;
      }
    });
  }

  public tableCustomViewChange(selectedOption: ISelectOption) {
    this.selectedCustomOption = selectedOption;
    this._customViewTriggered = true;
    this._updateFiltersByCustomView(selectedOption);
  }

  public fromDateSelected(value?: Date): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.FromDate, value);
  }

  public toDateSelected(value?: Date): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.ToDate, value);
  }

  public searchSubmitted(value?: string): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.Search, value);
  }

  private _updateFiltersByCustomView(selectedView: ISelectOption) {
    let filtersByKeyValue = {};
    switch (selectedView.id) {
      case InventoryCustomViewKey.applicationsRecentlyModified:
        filtersByKeyValue = {
          lastModifiedSinceDate: '30',
          category: 'application',
        };
        break;
      case InventoryCustomViewKey.automationsRecentlyModified:
        filtersByKeyValue = {
          lastModifiedSinceDate: '30',
          category: 'automation',
        };
        break;
      case InventoryCustomViewKey.newUsersCreation:
        filtersByKeyValue = {
          fromDate: moment().subtract(30, 'days').toISOString(),
          category: 'identity',
        };
        break;
      case InventoryCustomViewKey.inactiveUsers:
        filtersByKeyValue = {
          category: 'identity',
          type: 'user',
          resourceStatus: 'inactive',
        };
        break;
      case InventoryCustomViewKey.disabledConnections:
        filtersByKeyValue = {
          category: 'connection',
          resourceStatus: 'inactive',
        };
        break;
      case InventoryCustomViewKey.disabledAutomations:
        filtersByKeyValue = {
          category: 'automation',
          resourceStatus: 'inactive',
        };
        break;
      case InventoryCustomViewKey.externalIdentities:
        filtersByKeyValue = {
          category: 'identity',
          identityIsExternal: 'true',
        };
        break;
      case InventoryCustomViewKey.allBuilders:
        filtersByKeyValue = {
          category: 'identity',
          type: 'user',
          allBuilders: 'true',
        };
        break;
      case InventoryCustomViewKey.connectionCreatedFromCustomConnector:
        filtersByKeyValue = {
          category: 'connection',
          connectionConnectorType: 'customConnector',
        };
        break;
      case InventoryCustomViewKey.notInUseConnection:
        filtersByKeyValue = {
          category: 'connection',
          connectionInUse: 'false',
        };
        break;
      case InventoryCustomViewKey.automationUsesAiModel:
        filtersByKeyValue = {
          category: 'automation',
          automationUsesAiModel: 'true',
        };
        break;
      case InventoryCustomViewKey.teamsEnvironments:
        filtersByKeyValue = {
          category: 'environment',
          environmentType: 'teams',
        };
        break;
      case InventoryCustomViewKey.autonomousAgents:
        filtersByKeyValue = {
          category: 'bot',
          botType: 'autonomousAgent',
        };
        break;
      case InventoryCustomViewKey.declarativeAgents:
        filtersByKeyValue = {
          category: 'bot',
          botType: 'declarativeAgent',
        };
        break;
      case InventoryCustomViewKey.agentsUsingMcp:
        filtersByKeyValue = {
          category: 'bot',
          isMcpHost: 'true',
        };
        break;
      case InventoryCustomViewKey.automationUsesWebhook:
        filtersByKeyValue = {
          category: 'automation',
          automationUsesWebhook: 'true',
        };
        break;
      case InventoryCustomViewKey.automationUsesHttpStepsWithoutAuthentication:
        filtersByKeyValue = {
          category: 'automation',
          automationUsesHttpStepsWithoutAuthentication: 'true',
        };
        break;
      case InventoryCustomViewKey.agentTriggers:
        filtersByKeyValue = {
          category: 'automation',
          automationIsBotTrigger: 'true',
        };
        break;
      case InventoryCustomViewKey.orgWideAccess:
        filtersByKeyValue = { orgWideAccess: 'true' };
        break;
      case InventoryCustomViewKey.publicAccess:
        filtersByKeyValue = { publicAccess: 'true' };
        break;
      case InventoryCustomViewKey.shadowAi:
        filtersByKeyValue = {
          platform: 'devices',
        };
        break;
      case InventoryCustomViewKey.privilegedAgents:
        filtersByKeyValue = {
          category: 'bot',
          hasWritePermission: 'true',
        };
        break;
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(filtersByKeyValue);
    this._router.navigate([], { queryParams: { q: filtersQuery } });
  }
}
